export type TimeOfDay = "morning" | "afternoon" | "evening";

/**
 * Single source of truth for the Morning / Afternoon / Evening buckets on the
 * train list: the filter chips above it and the badge on each train card both
 * go through here, so the two can never disagree.
 *
 * morning   00:00 - 12:00
 * afternoon 12:01 - 18:00
 * evening   18:01 - 23:59
 *
 * Note this is deliberately not shared with the departure-time pickers in
 * DepartureTimeInput / PersonalizeDrawerValue — those group time *slots* on
 * their own boundaries and are a separate concern.
 *
 * A missing minute is read as :00. That is a deliberate fix, not parity: the
 * old inline switch compared against a NaN minute, so a bare "12" or "18" fell
 * through every branch and vanished from all three filters. "24:00" likewise
 * used to count as evening and is now null — the API sends date-time and the
 * UI formats it, so an hour of 24 should not reach us.
 *
 * Returns null for anything else we cannot parse, so callers decide what to do
 * with it (the filter drops the train, the badge renders nothing).
 */
export function getTimeOfDay(time: string | undefined | null): TimeOfDay | null {
    const [hour, minute] = String(time ?? "")
        .split(":")
        .map((part) => parseInt(part, 10));

    if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;

    const minutes = Number.isInteger(minute) ? minute : 0;

    if (hour < 12 || (hour === 12 && minutes === 0)) return "morning";
    if (hour < 18 || (hour === 18 && minutes === 0)) return "afternoon";
    return "evening";
}
